import { useEffect, useRef, useCallback } from 'react'
import { useSetAtom } from 'jotai'
import { useNavigate } from 'react-router'
import { currentClassIdAtom, currentSessionIdAtom, historyResetSessionIdsAtom, sessionsAtom } from '../state/store'
import { useWebSocket } from '../contexts/WebSocketContext'

/**
 * Syncs the URL chatId param → currentSessionIdAtom.
 * Called from route pages that know their chatId.
 *
 * Also handles WS subscribe/unsubscribe on chatId transitions.
 */
export function useChatRouteSync(chatId: string | null, classId: string | null = null) {
  const setCurrentSessionId = useSetAtom(currentSessionIdAtom)
  const setCurrentClassId = useSetAtom(currentClassIdAtom)
  const setHistoryResetSessionIds = useSetAtom(historyResetSessionIdsAtom)
  const setSessions = useSetAtom(sessionsAtom)
  const { subscribe, unsubscribe } = useWebSocket()
  const prevChatIdRef = useRef<string | null>(null)

  useEffect(() => {
    setCurrentClassId(classId)
  }, [classId, setCurrentClassId])

  useEffect(() => {
    const prev = prevChatIdRef.current

    // Skip if unchanged
    if (prev === chatId) return

    // Unsubscribe from old session
    if (prev) {
      unsubscribe(prev)
    }

    // Update atom
    setCurrentSessionId(chatId)

    // Clear any stale local messages before resubscribing so the chat is
    // fully rebuilt from the server-provided history payload.
    if (chatId && prev) {
      setHistoryResetSessionIds((prevIds: Set<string>) => {
        const next = new Set(prevIds)
        next.add(chatId)
        return next
      })
      setSessions((prevSessions) =>
        prevSessions.map((session) =>
          session.id === chatId
            ? { ...session, messages: [], hasMore: undefined }
            : session,
        ),
      )
    }

    // Subscribe to new session
    if (chatId) {
      subscribe(chatId)
    }

    prevChatIdRef.current = chatId
  }, [chatId, setCurrentSessionId, setHistoryResetSessionIds, setSessions, subscribe, unsubscribe])
}

/**
 * Provides navigation functions that use router.push instead of
 * directly setting the atom. Components call these instead of
 * the old selectSession/setCurrentSessionId patterns.
 */
export function useChatNavigation() {
  const navigate = useNavigate()

  const navigateToChat = useCallback(
    (chatId: string, classId?: string | null) => {
      navigate(classId ? `/classes/${classId}/chats/${chatId}` : `/c/${chatId}`)
    },
    [navigate],
  )

  const navigateToHome = useCallback(() => {
    navigate('/')
  }, [navigate])

  const navigateToClassHome = useCallback((classId: string) => {
    navigate(`/classes/${classId}`)
  }, [navigate])

  return { navigateToChat, navigateToHome, navigateToClassHome }
}
